import { PinnedSortBy } from '../types';

/**
 * Sorts a copy of the given array based on a {@link PinnedSortBy} value.
 *
 * Generic so it works with any entity (e.g. {@link OrganizationStudy},
 * {@link OrganizationSite}) — callers provide field accessors for the date and
 * name values used in the sort comparison.
 *
 * @typeParam T - Entity type being sorted.
 * @param items - Source array to sort.
 * @param sort - Active sort order derived from the `pinnedSort` URL query param.
 * @param options - Field accessors: `getDate` for NEWEST/OLDEST, `getName` for NAME_ASC/NAME_DESC.
 * @returns A new sorted array, or the original reference when `sort` is `null`.
 */
export function sortByPinnedParam<T>(
  items: T[],
  sort: PinnedSortBy,
  options: { getDate: (item: T) => string; getName: (item: T) => string },
): T[] {
  if (!sort) return items;
  const { getDate, getName } = options;
  switch (sort) {
    case 'NEWEST':
      return items.toSorted(
        (a, b) =>
          new Date(getDate(b)).getTime() - new Date(getDate(a)).getTime(),
      );
    case 'OLDEST':
      return items.toSorted(
        (a, b) =>
          new Date(getDate(a)).getTime() - new Date(getDate(b)).getTime(),
      );
    case 'NAME_ASC':
      return items.toSorted((a, b) => getName(a).localeCompare(getName(b)));
    case 'NAME_DESC':
      return items.toSorted((a, b) => getName(b).localeCompare(getName(a)));
    default:
      return items;
  }
}

/** Maps a {@link PinnedSortBy} value to the server-side sort param for the given entity type. */
export function pinnedSortToSortParam(
  sort: PinnedSortBy | null,
  entity: 'study' | 'site',
): { field: string; dir: string } | null {
  const dateField = entity === 'study' ? 'created_dt' : 'approval_dt';
  const nameField = entity === 'study' ? 'title' : 'study__title';
  switch (sort) {
    case 'NEWEST':
      return { field: dateField, dir: 'desc' };
    case 'OLDEST':
      return { field: dateField, dir: 'asc' };
    case 'NAME_ASC':
      return { field: nameField, dir: 'asc' };
    case 'NAME_DESC':
      return { field: nameField, dir: 'desc' };
    default:
      return null;
  }
}
